import { TemplateFieldType, type TemplateField } from '@/api';
import type { AnnotationDraft } from '@/composables/contractGeneration/useTemplateAnnotations';
import {
  entityAliasFromSchemaKey,
  type FlattenedField,
} from '@/utils/contractGeneration/templateSchemaFields';

/**
 * Pure helpers for editing a detected field from the Editor tab: map a
 * detected schema field onto a config-drawer draft, and find the field's
 * raw `{{ … }}` tags in the document body text so an edit can be applied
 * to every occurrence. No Office.js in here — the Word interop lives in
 * `templateWord.ts`.
 */

const EDITABLE_FIELD_TYPES: ReadonlySet<TemplateFieldType | 'unknown'> = new Set([
  TemplateFieldType.STRING,
  TemplateFieldType.NUMBER,
  TemplateFieldType.DATE,
  TemplateFieldType.BOOLEAN,
  TemplateFieldType.SELECTION,
]);

/**
 * Whether a detected-fields row can carry the edit button. Only scalar
 * leaves whose tag is addressable by path qualify — containers have no
 * inline tag of their own, and list item fields render through the loop
 * variable (`{{ item.x }}`), so their tag can't be derived from the path.
 * A scalar that only appears inside `{% if %}` tags passes this static
 * check and is caught by the document scan when clicked.
 */
export function isEditableDetectedField(field: FlattenedField): boolean {
  if (field.hasChildren) return false;
  if (field.path.includes('[]')) return false;
  return EDITABLE_FIELD_TYPES.has(field.type);
}

/** Walk the schema map along a dotted path (`[]` paths are never passed here). */
export function resolveFieldAtPath(
  fields: { [key: string]: TemplateField } | null | undefined,
  path: string
): TemplateField | null {
  let scope = fields ?? null;
  let current: TemplateField | null = null;
  for (const segment of path.split('.')) {
    current = scope?.[segment] ?? null;
    if (!current) return null;
    scope = current.fields ?? null;
  }
  return current;
}

/**
 * Map a detected schema field onto a drawer draft. The binding is derived
 * from the path root: no dot → primitive, entity-typed root → entity role
 * (alias recovered from the `<entity_type>_<alias>` key), anything else →
 * object group. `default`/`choices` filters are dropped from the copied
 * filter list — they are carried as `default_value`/`selection_options`
 * and re-emitted by `renderJinjaPreview`, so keeping them would double
 * them up in the rendered tag.
 */
export function buildDraftForDetectedField(
  path: string,
  leaf: TemplateField,
  fields: { [key: string]: TemplateField | undefined },
  localId: string,
  anchor: AnnotationDraft['anchor']
): AnnotationDraft {
  const root = path.split('.')[0];
  // The path's root may not exist in the schema, hence the `| undefined`
  // index signature on `fields`.
  const rootField: TemplateField | undefined = fields[root];
  const rest = path.slice(root.length + 1);

  let fieldName: string | null = null;
  let binding: AnnotationDraft['binding'] = null;
  if (!rest) {
    fieldName = path;
    binding = { kind: 'primitive' };
  } else if (
    rootField?.type === TemplateFieldType.LEGAL_ENTITY ||
    rootField?.type === TemplateFieldType.NATURAL_PERSON
  ) {
    binding = {
      kind: 'entity',
      entity_type: rootField.type,
      instance_alias: entityAliasFromSchemaKey(root, rootField),
      sub_field: rest,
    };
  } else {
    binding = { kind: 'object', instance_alias: root, sub_field: rest };
  }

  return {
    local_id: localId,
    kind: 'inline',
    anchor,
    field_name: fieldName,
    binding,
    origin: 'extracted',
    filters: (leaf.filters ?? [])
      .filter(filter => filter.name !== 'default' && filter.name !== 'choices')
      .map(filter => ({ name: filter.name, args: [...(filter.args ?? [])] })),
    default_value: leaf.default_value ?? null,
    selection_options: leaf.selection_options ? [...leaf.selection_options] : null,
    required: Boolean(leaf.required),
    condition: null,
  };
}

/** One representative raw tag per whitespace-equivalence class, plus the total count. */
export interface FieldTagExtraction {
  finds: string[];
  count: number;
}

// Matches one whole inline Jinja tag: literal `{{`, then any run of
// characters that contains no brace (`[^{}]*`), then literal `}}`.
// Excluding braces from the middle is what keeps a match from spilling
// past its own tag — with `.*?` instead, malformed input like `{{ a b`
// followed by `{{ c }}` would match `{{ a b ... c }}` as one tag. The
// trade-off: a brace inside a quoted filter arg (`{{ x | default("a}b") }}`)
// breaks that tag's match — acceptable, since such args are rare and the
// field is then simply reported as not found rather than mis-replaced.
const INLINE_TAG_RE = /\{\{[^{}]*\}\}/g;

/**
 * Extract every inline `{{ … }}` tag whose expression root is exactly
 * `path` — i.e. the path is followed by nothing or by a `|` filter chain,
 * so `client.name_extra` and `client.name.upper()` don't match
 * `client.name`. Finds are deduped on a whitespace-stripped key: the Word
 * search that consumes them runs with `ignoreSpace`, so one representative
 * per class matches every whitespace variant in the document.
 */
export function extractFieldTagOccurrences(bodyText: string, path: string): FieldTagExtraction {
  const finds: string[] = [];
  const seen = new Set<string>();
  let count = 0;
  for (const match of bodyText.matchAll(INLINE_TAG_RE)) {
    const raw = match[0];
    const inner = raw.slice(2, -2).trim();
    if (!inner.startsWith(path)) continue;
    const rest = inner.slice(path.length).trimStart();
    if (rest !== '' && !rest.startsWith('|')) continue;
    count += 1;
    const key = raw.replace(/\s+/g, '');
    if (!seen.has(key)) {
      seen.add(key);
      finds.push(raw);
    }
  }
  return { finds, count };
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
}

/**
 * Whether any `{% … %}` tag references `root` as a whole word — a field
 * edit only rewrites inline tags, so after a rename this flags conditions
 * and loops that still point at the old variable.
 */
export function blockTagsReferenceRoot(bodyText: string, root: string): boolean {
  const rootRe = new RegExp(String.raw`\b${escapeRegExp(root)}\b`);
  for (const match of bodyText.matchAll(/\{%[^{}]*?%\}/g)) {
    if (rootRe.test(match[0])) return true;
  }
  return false;
}

/**
 * First path segment of the variable a draft renders — mirrors how
 * `renderJinjaPreview` names it (`<entity_type>_<alias>` for entity roles,
 * the group alias for objects, the field name for primitives).
 */
export function draftVariableRoot(draft: AnnotationDraft): string {
  const binding = draft.binding ?? null;
  if (binding?.kind === 'entity') return `${binding.entity_type}_${binding.instance_alias}`;
  if (binding?.kind === 'object') return binding.instance_alias;
  return (draft.field_name ?? 'field').split('.')[0];
}
